/*
 * Ariane offerte — synchronisation de la ligne cadeau dans le panier.
 *
 * Principe : ce script n'ajoute et ne retire QUE des lignes. Il ne fixe jamais
 * un prix. La gratuite vient exclusivement de la remise automatique BXGY
 * ("achete 1 ventilateur, obtient 1 Ariane a -100%"). Si le script echoue, le
 * pire cas est un client qui voit 69 EUR affiches, jamais une remise accordee
 * a tort.
 *
 * Methode : le cadeau est ajoute AU MOMENT ou des unites de ventilateur
 * entrent au panier, et jamais re-ajoute ensuite. On tient pour cela un
 * filigrane `servi` : le nombre d'unites de ventilateur pour lesquelles un
 * cadeau a deja ete propose. Tant que ce nombre n'augmente pas, le script
 * n'ajoute rien. Consequence voulue (demande de Maxime, 10/08/2026) : un
 * client qui retire son Ariane du panier la garde retiree.
 *
 * Le script continue en revanche de PLAFONNER la ligne cadeau. Le plafond vaut
 * min(nombre de ventilateurs au panier, `max_gift_qty`). Sans lui, retirer un
 * ventilateur laisserait un cadeau orphelin que la remise BXGY ne couvre plus,
 * et le client le paierait plein tarif. Le plafond ne remonte jamais la
 * quantite, il ne fait que la baisser.
 *
 * `max_gift_qty` DOIT valoir exactement le `usesPerOrderLimit` de la remise BXGY
 * cote Shopify. La BXGY est la seule a mettre un prix a zero : tout cadeau ajoute
 * au-dela de sa limite est FACTURE. Les deux chiffres se modifient ensemble.
 * Operation en cours : Suspension Aurora offerte, 1 par commande, 219 EUR.
 *
 * Le filigrane vit dans localStorage, cle par `cart.token`. Un nouveau panier
 * (apres commande) repart donc a zero et le cadeau suivant est bien propose.
 * Si localStorage est indisponible (Safari prive, stockage purge), on retombe
 * en memoire : le filigrane ne survit alors pas au changement de page, ce qui
 * ramene au comportement d'avant. Jamais pire, seulement moins persistant.
 *
 * La ligne cadeau porte la propriete `_cadeau_ariane`. Le prefixe underscore
 * la masque au client tout en la conservant dans la commande, ce qui permet
 * a l'atelier de la voir sur le bon de preparation. Une Ariane achetee
 * volontairement par le client n'a pas cette propriete : elle n'est jamais
 * touchee par ce script.
 *
 * Configuration injectee par Liquid dans #ariane-offerte-config (voir
 * snippets/ariane-offerte-config.liquid). La liste des variantes de
 * ventilateur vient de la collection, elle se met donc a jour toute seule
 * quand un modele est ajoute ou retire.
 */
(function () {
  'use strict';

  var CONFIG_EL = 'ariane-offerte-config';
  var GIFT_PROPERTY = '_cadeau_ariane';
  var GIFT_PROPERTY_VALUE = 'offert';
  var DEBOUNCE_MS = 250;

  function readConfig() {
    var el = document.getElementById(CONFIG_EL);
    if (!el) return null;
    try {
      var cfg = JSON.parse(el.textContent);
      if (!cfg.enabled) return null;
      if (!cfg.gift_variant_id) return null;
      var hasType = typeof cfg.trigger_product_type === 'string' && cfg.trigger_product_type.length > 0;
      var hasIds = Array.isArray(cfg.trigger_variant_ids) && cfg.trigger_variant_ids.length > 0;
      if (!hasType && !hasIds) return null;
      return cfg;
    } catch (e) {
      return null;
    }
  }

  var config = readConfig();
  if (!config) return;

  var giftVariantId = Number(config.gift_variant_id);

  /* Plafond de cadeaux par commande. Defaut 1, et non « pas de plafond » :
     si la config est illisible ou incomplete, l'erreur doit couter au plus un
     cadeau, jamais un par ventilateur. */
  var maxGiftQty = Number(config.max_gift_qty);
  if (!isFinite(maxGiftQty) || maxGiftQty < 1) maxGiftQty = 1;
  var triggerType = config.trigger_product_type || null;
  var triggerIds = {};
  (config.trigger_variant_ids || []).forEach(function (id) {
    triggerIds[Number(id)] = true;
  });

  /* Un ventilateur se reconnait a son `product_type`, convention deja utilisee
     ailleurs dans ce theme (cart-drawer, PDP). La liste de variantes issue de
     la collection sert de filet au cas ou `product_type` serait absent du
     payload de /cart.js. */
  function isTrigger(item) {
    if (triggerType && item.product_type === triggerType) return true;
    return triggerIds[Number(item.variant_id)] === true;
  }

  var busy = false;
  var pending = null;

  /* Coupe-circuit. Si la ligne cadeau se retrouve facturee (remise expiree,
     code non cumulable saisi par le client, mauvaise configuration), on la
     retire et on cesse de la reproposer pour cette page. Sans ce garde-fou,
     la reconciliation la remettrait en boucle et le client paierait 69 EUR
     pour un cadeau. Ne jamais desactiver. */
  var disabled = false;

  /* `Shopify.routes.root` vaut '/' ou '/fr/' selon la locale. Repli defensif. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function isGiftLine(item) {
    return (
      Number(item.variant_id) === giftVariantId &&
      item.properties &&
      item.properties[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE
    );
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  /* Nombre d'unites de ventilateur au panier. Sert de deux facons : c'est ce
     qui fait monter le filigrane, et c'est le plafond de la ligne cadeau. */
  function fanUnits(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (isTrigger(item)) total += item.quantity;
    });
    return total;
  }

  function currentGiftLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i])) {
        return {
          key: cart.items[i].key,
          quantity: cart.items[i].quantity,
          charged: cart.items[i].final_line_price > 0
        };
      }
    }
    return null;
  }

  function addGift(quantity) {
    var properties = {};
    properties[GIFT_PROPERTY] = GIFT_PROPERTY_VALUE;
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: giftVariantId, quantity: quantity, properties: properties }]
      })
    });
  }

  /* On cible la ligne par sa `key` et non par l'id de variante : c'est ce qui
     garantit qu'une Ariane achetee volontairement, qui porte la meme variante
     mais pas la propriete cadeau, n'est jamais modifiee. */
  function setGiftQuantity(key, quantity) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  /* ---- Filigrane `servi` ---------------------------------------------------
     Nombre d'unites de ventilateur pour lesquelles un cadeau a deja ete
     propose. C'est lui qui distingue « le client n'a pas encore eu son
     cadeau » de « le client l'a eu et l'a retire ». Sans lui, les deux etats
     sont indiscernables dans /cart.js et le script boucle. */
  var STORAGE_PREFIX = 'ariane_offerte_servi:';
  var memoryMark = {};

  function storage() {
    try {
      var s = window.localStorage;
      s.setItem(STORAGE_PREFIX + 'probe', '1');
      s.removeItem(STORAGE_PREFIX + 'probe');
      return s;
    } catch (e) {
      return null;
    }
  }

  function readMark(token) {
    var s = storage();
    var raw = s ? s.getItem(STORAGE_PREFIX + token) : memoryMark[token];
    if (raw === null || raw === undefined) return null;
    var n = Number(raw);
    return isFinite(n) && n >= 0 ? n : null;
  }

  /* On purge au passage les filigranes des autres paniers : apres une
     commande, Shopify emet un nouveau `cart.token` et l'ancien ne sert plus. */
  function writeMark(token, value) {
    var s = storage();
    if (!s) { memoryMark = {}; memoryMark[token] = String(value); return; }
    try {
      for (var i = s.length - 1; i >= 0; i--) {
        var k = s.key(i);
        if (k && k.indexOf(STORAGE_PREFIX) === 0 && k !== STORAGE_PREFIX + token) s.removeItem(k);
      }
      s.setItem(STORAGE_PREFIX + token, String(value));
    } catch (e) { /* quota : le filigrane retombe en memoire au prochain read */ }
  }

  function reconcile() {
    if (busy || disabled) return Promise.resolve(false);
    busy = true;

    return getCart()
      .then(function (cart) {
        var fans = fanUnits(cart);
        /* Ce que la remise BXGY accepte de couvrir sur ce panier. C'est la seule
           quantite de cadeau qui puisse valoir 0 EUR. */
        var couverts = Math.min(fans, maxGiftQty);
        var line = currentGiftLine(cart);
        var have = line ? line.quantity : 0;
        var token = cart.token || 'sans-token';

        /* Coupe-circuit : cadeau facture. On le retire et on s'arrete la. */
        if (line && line.charged) {
          disabled = true;
          return setGiftQuantity(line.key, 0).then(function () { return true; });
        }

        /* Premier passage sur ce panier : on deduit le filigrane de l'etat
           observe. Un panier qui porte deja son cadeau est considere servi,
           sinon on part de zero et le cadeau sera propose. */
        var servi = readMark(token);
        if (servi === null) {
          servi = Math.min(have, fans);
          writeMark(token, servi);
        }

        /* Panier vide de ventilateurs : on retire le cadeau et on remet le
           filigrane a zero, pour qu'un futur ajout redonne droit au cadeau. */
        if (fans === 0) {
          if (servi !== 0) writeMark(token, 0);
          if (!line) return false;
          return setGiftQuantity(line.key, 0).then(function () { return true; });
        }

        /* Des ventilateurs viennent d'entrer : c'est le SEUL cas d'ajout. */
        if (fans > servi) {
          var target = Math.min(have + (fans - servi), couverts);
          writeMark(token, fans);
          if (target === have) return false;
          if (!line) return addGift(target).then(function () { return true; });
          return setGiftQuantity(line.key, target).then(function () { return true; });
        }

        /* Cadeau en trop : ventilateurs partis, ou plafond abaisse entre deux
           visites sur un panier deja servi. On plafonne, on ne remonte jamais. */
        if (have > couverts) {
          writeMark(token, fans);
          return setGiftQuantity(line.key, couverts).then(function () { return true; });
        }

        /* Reste le cas `have < fans` a filigrane egal : le client a retire son
           cadeau. On ne le remet pas. C'est le comportement demande. */
        return false;
      })
      .then(function (changed) {
        busy = false;
        if (changed) refreshCartSections();
        return changed;
      })
      .catch(function () {
        /* Echec reseau : on ne reessaie pas en boucle. La prochaine
           reconciliation, declenchee par la prochaine action du client,
           corrigera l'etat. */
        busy = false;
        return false;
      });
  }

  function schedule() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      reconcile();
    }, DEBOUNCE_MS);
  }

  /* Re-render des surfaces panier, meme approche que cart-checkbox-upsell.js
     deja en production sur ce theme. */
  function refreshCartSections() {
    var targets = [
      { section: 'cart-drawer', selector: '#CartDrawer', inner: '.drawer__inner' },
      { section: 'main-cart-items', selector: '#main-cart-items', inner: '.js-contents' },
      { section: 'main-cart-footer', selector: '#main-cart-footer', inner: '.js-contents' },
      { section: 'cart-icon-bubble', selector: '#cart-icon-bubble', inner: null }
    ];

    targets.forEach(function (t) {
      var container = document.querySelector(t.selector);
      if (!container) return;

      fetch(root() + '?section_id=' + t.section, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (t.inner === null) {
            container.innerHTML = html;
            return;
          }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector(t.inner);
          var current = container.querySelector(t.inner) || container;
          if (fresh) current.innerHTML = fresh.innerHTML;
        })
        .catch(function () { /* surface non presente sur cette page */ });
    });
  }

  /* Declencheurs.
     Le theme emet `cartUpdated`, mais tous les chemins ne l'emettent pas de
     maniere fiable. On surveille donc aussi les appels reseau au panier, en
     ignorant les notres pour ne pas boucler. */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartWrite = CART_WRITE.test(String(url).split('?')[0]);
    var result = nativeFetch.apply(this, arguments);
    if (isCartWrite && !busy) {
      result.then(function () { schedule(); }).catch(function () {});
    }
    return result;
  };

  document.addEventListener('cartUpdated', schedule);
  document.addEventListener('DOMContentLoaded', schedule);
  if (document.readyState !== 'loading') schedule();
})();
